import * as React from 'react';
import { st, classes } from './CircularProgressBarCore.st.css';
import { Arc } from './Arc';
import { dataHooks } from './constants';
import { filterDataProps } from '../../utils/propsUtils';
import { ProgressLabel } from './ProgressLabel';

export interface CircularProgressBarCoreProps {
  /** represent the progress state in percentages (0 - no progress, 100 - progress completed) */
  value?: number | string;
  /** should be true if had failure during the progress */
  error?: boolean;
  /** label to display when an error happens */
  errorLabel?: string;
  /** when set to true, an indication of the progress state will be presented along side the progress bar */
  showProgressIndication?: boolean;
  /** an indication icon (any react component) that will be presented when 'error' and 'showProgressIndication' are set to true */
  errorIcon?: React.JSX.Element;
  /** an indication icon (any react component) that will be presented when 'showProgressIndication' are set to true and 'value' is 100 */
  successIcon?: React.JSX.Element;
  /** custom text to display. will replace the progress indication if provided */
  label?: string;
  /** Placement of the label in the progress bar */
  labelPlacement?: 'center' | 'bottom';
  /** circle size in pixels */
  size?: number | string;
  /** hook for testing purposes */
  'data-hook'?: string;
  /** Skin of the bar */
  'data-skin'?: 'standard' | 'premium' | 'success';
  className?: string;
}

const FULL_PROGRESS = 100;
const NO_PROGRESS = 0;
const VIEWBOX_SIZE = 54;

const resolveIndicationElement = (
  props: CircularProgressBarCoreProps & {
    shouldShowProgressIndication: boolean;
  },
) => {
  const wrapped = (dataHook: string, children: React.JSX.Element) => (
    <div data-hook={dataHook} className={classes.statusIndicator}>
      {children}
    </div>
  );

  if (props.error && props.errorIcon) {
    return wrapped(dataHooks.errorIcon, props.errorIcon);
  }

  if (props.value === FULL_PROGRESS && props.successIcon) {
    return wrapped(dataHooks.successIcon, props.successIcon);
  }

  if (
    props.labelPlacement === 'center' &&
    (props.shouldShowProgressIndication || props.label)
  ) {
    return wrapped(
      props.label ? dataHooks.label : dataHooks.progressIndicator,
      <ProgressLabel {...props} />,
    );
  }
  return null;
};

const normalizeValue = (value: string | number) => {
  return typeof value === 'number'
    ? value
    : parseInt(value, 10)
    ? parseInt(value, 10)
    : NO_PROGRESS;
};

const normalizeSize = (size: string | number) => {
  const intSize = typeof size === 'number' ? size : parseInt(size, 10);
  return intSize && intSize > 0 ? intSize : VIEWBOX_SIZE;
};

export const convertToPercentageString = (value: number) => {
  return `${Math.floor(value)}%`;
};

const renderArcs = (
  props: CircularProgressBarCoreProps & {
    shouldShowProgressIndication: boolean;
  },
) => {
  const { value, size } = props;
  const normalizedSize = normalizeSize(size || VIEWBOX_SIZE);
  const normalizedValue = normalizeValue(value || NO_PROGRESS);
  return (
    <div
      className={classes.arcsContainer}
      style={{ width: `${normalizedSize}px`, height: `${normalizedSize}px` }}
    >
      {resolveIndicationElement(props)}
      <Arc
        data-hook={dataHooks.progressArcBackground}
        value={FULL_PROGRESS}
        className={classes.backArc}
        strokeWidth={4}
        size={normalizedSize}
      />
      <Arc
        data-hook={dataHooks.progressArcForeground}
        value={normalizedValue}
        className={classes.foreArc}
        strokeWidth={4}
        size={normalizedSize}
      />
    </div>
  );
};

const normalizeProps = (props: CircularProgressBarCoreProps) => {
  const value = normalizeValue(props.value || NO_PROGRESS);

  if (value >= FULL_PROGRESS) {
    return { ...props, value: FULL_PROGRESS };
  }

  if (value < NO_PROGRESS) {
    return { ...props, value: NO_PROGRESS };
  }

  return { ...props, value };
};

export const CircularProgressBarCore: React.FunctionComponent<
  CircularProgressBarCoreProps
> = ({
  value = NO_PROGRESS,
  size = VIEWBOX_SIZE,
  labelPlacement = 'bottom',
  ...propsWithNoDefaults
}: CircularProgressBarCoreProps) => {
  const props: CircularProgressBarCoreProps = {
    value,
    size,
    labelPlacement,
    ...propsWithNoDefaults,
  };
  const { label, error, showProgressIndication } = props;
  const _props = normalizeProps(props);
  const success = _props.value === FULL_PROGRESS;
  const displayValue =
    error && _props.errorLabel
      ? _props.errorLabel
      : convertToPercentageString(_props.value);

  const shouldShowProgressIndication = Boolean(
    showProgressIndication && !label,
  );
  return (
    <div
      className={st(classes.root, { error, success }, _props.className)}
      data-error={error}
      {...filterDataProps(props)}
    >
      {renderArcs({ ..._props, shouldShowProgressIndication })}
      {label && labelPlacement === 'bottom' && (
        <div data-hook={dataHooks.label} className={classes.label}>
          {label}
        </div>
      )}
      {shouldShowProgressIndication && labelPlacement === 'bottom' && (
        <div
          data-hook={dataHooks.progressIndicator}
          className={classes.progressIndicator}
        >
          {displayValue}
        </div>
      )}
    </div>
  );
};

CircularProgressBarCore.displayName = 'CircularProgressBarCore';
